import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, RotateCcw } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { Helmet } from "react-helmet-async";
import { useLang } from "../i18n";
import VehicleCard from "../components/VehicleCard";
import Reveal from "../components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CATS = ["cars", "vans", "trucks", "trailers", "forklifts"];
const SORTS = ["newest", "price-asc", "price-desc", "year-desc", "mileage-asc"];

export default function Inventory() {
  const { t, lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const view = searchParams.get("view") || "stock";
  const category = searchParams.get("category") || "";
  const brand = searchParams.get("brand") || "";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "newest";
  const offersOnly = searchParams.get("offers") === "1";

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/vehicles/brands")
  .then((r) => {
    const data = r.data;
    setBrands(
      Array.isArray(data)
        ? data
        : Array.isArray(data?.brands)
        ? data.brands
        : []
    );
  })
  .catch(() => setBrands([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (brand) params.brand = brand;
    if (search) params.search = search;
    if (offersOnly) params.offer = true;
    if (sort && sort !== "newest") params.sort = sort;
    api.get("/vehicles", { params })
      .then((r) => {
  const data = r.data;
  const vehicles = Array.isArray(data)
    ? data
    : Array.isArray(data?.vehicles)
    ? data.vehicles
    : [];
  setVehicles(vehicles);
})
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, [view, category, brand, search, sort, offersOnly]);

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== search) setParam("search", searchInput);
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const reset = () => {
    const next = new URLSearchParams();
    if (view === "sold") next.set("view", "sold");
    setSearchParams(next, { replace: true });
  };

  const hasFilters = useMemo(() => category || brand || search || offersOnly, [category, brand, search, offersOnly]);

  return (
    <div data-testid="inventory-page" className="pt-32 pb-24 min-h-screen">
      <Seo title={t("seo.invTitle")} description={t("seo.invDesc")} />
      {!loading && vehicles.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              itemListElement: vehicles.map((v, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `${window.location.origin}${lang === "ro" ? "/ro" : ""}/inventory/${v.id}`,
                name: v.title,
              })),
            })}
          </script>
        </Helmet>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50">{t("inv.title")}</h1>
          <p className="mt-5 text-slate-400 max-w-xl">{t("inv.sub")}</p>
        </Reveal>

        <div className="mt-10 flex gap-3" data-testid="inventory-view-tabs">
          <button
            data-testid="inventory-tab-stock"
            onClick={() => setParam("view", "")}
            className={`rounded-full px-6 py-2.5 font-display font-semibold transition-colors ${
              view !== "sold" ? "bg-[#4499F7] text-[#0A0B10]" : "text-slate-300 hover:text-[#4499F7] border border-white/15"
            }`}
          >
            {t("inv.tabStock")}
          </button>
          <button
            data-testid="inventory-tab-sold"
            onClick={() => setParam("view", "sold")}
            className={`rounded-full px-6 py-2.5 font-display font-semibold transition-colors ${
              view === "sold" ? "bg-[#4499F7] text-[#0A0B10]" : "text-slate-300 hover:text-[#4499F7] border border-white/15"
            }`}
          >
            {t("inv.tabSold")}
          </button>
        </div>

        <div className="mt-8 space-y-6" data-testid="inventory-filters">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="inventory-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("inv.search")}
                className="w-full bg-[#12141D] border border-white/10 rounded-full pl-11 pr-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[rgba(68,153,247,0.5)] transition-colors"
              />
            </div>
            <div className="flex gap-4">
              <Select value={brand || "all"} onValueChange={(v) => setParam("brand", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="inventory-brand-select" className="w-full lg:w-52 bg-[#12141D] border-white/10 rounded-full h-[50px] text-slate-200">
                  <SelectValue placeholder={t("inv.allBrands")} />
                </SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-slate-200">
                  <SelectItem value="all">{t("inv.allBrands")}</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={(v) => setParam("sort", v === "newest" ? "" : v)}>
                <SelectTrigger data-testid="inventory-sort-select" className="w-full lg:w-56 bg-[#12141D] border-white/10 rounded-full h-[50px] text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-slate-200">
                  {SORTS.map((s) => (
                    <SelectItem key={s} value={s}>{t(`inv.sort.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="inventory-filter-category-all"
              onClick={() => setParam("category", "")}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
                !category
                  ? "bg-[#4499F7] text-[#0A0B10] border-[#4499F7]"
                  : "border-white/15 text-slate-300 hover:border-[rgba(68,153,247,0.5)] hover:text-[#4499F7]"
              }`}
            >
              {t("inv.allCats")}
            </button>
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`inventory-filter-category-${c}`}
                onClick={() => setParam("category", category === c ? "" : c)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
                  category === c
                    ? "bg-[#4499F7] text-[#0A0B10] border-[#4499F7]"
                    : "border-white/15 text-slate-300 hover:border-[rgba(68,153,247,0.5)] hover:text-[#4499F7]"
                }`}
              >
                {t(`cats.${c}`)}
              </button>
            ))}
            <button
              data-testid="inventory-filter-offers"
              onClick={() => setParam("offers", offersOnly ? "" : "1")}
              className={`rounded-full px-5 py-2.5 text-sm font-bold border uppercase tracking-wide transition-colors ${
                offersOnly
                  ? "bg-amber-400 text-[#0A0B10] border-amber-400"
                  : "border-amber-400/40 text-amber-300 hover:border-amber-400 hover:text-amber-200"
              }`}
            >
              {t("inv.offersOnly")}
            </button>
            {hasFilters && (
              <button
                data-testid="inventory-reset-btn"
                onClick={reset}
                className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-slate-400 hover:text-[#4499F7] transition-colors"
              >
                <RotateCcw size={14} />
                {t("inv.reset")}
              </button>
            )}
          </div>
        </div>

        <p data-testid="inventory-count" className="mt-10 font-mono-num text-xs tracking-[0.25em] uppercase text-slate-500">
          {vehicles.length} {t("inv.results")}
        </p>

        {loading ? (
          <p className="mt-16 text-slate-500" data-testid="inventory-loading">{t("common.loading")}</p>
        ) : vehicles.length === 0 ? (
          <div data-testid="inventory-empty" className="mt-16 text-center border border-dashed border-white/10 rounded-3xl py-24">
            <p className="text-slate-400">{t("inv.empty")}</p>
            <button
              data-testid="inventory-empty-reset-btn"
              onClick={reset}
              className="mt-6 text-sm font-semibold text-[#4499F7] hover:text-[#6FB1FA] transition-colors"
            >
              {t("inv.reset")}
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="inventory-grid">
            {vehicles.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
